import React from "react";
import { useNavigate } from "react-router-dom";
import { dialog } from "../../components/ui/Dialog";
import { useEtatGlobal } from "../EtatGlobal.js";
import "./Procedures.css";
import {
  PROC_EV, PROC_EV_FILTERS, PROC_SPACE_ICON, PROC_TEMPLATES,
  procCur, procFmt, procIaAutorise, procNorm, procRel, procText, procWithToc,
} from "./procHelpers";
import {
  procAclOf, procCan, procCanSpace, procIsRestricted, procName, procSetPeople, procWho,
} from "./procPeople";
import { useProcStore } from "./useProcStore";
import { ProcAssistant, ProcMascot } from "./ProcAssistant";
import { Icon, PrIco, ProcAv, ProcStatus } from "./ProcUi";
import ProcEditor from "./ProcEditor";
import ProcHistory from "./ProcHistory";
import ProcAccessModal from "./ProcAccessModal";
import ProcRedactionIA from "./ProcRedactionIA";
import ProcPartageModal from "./ProcPartageModal";

// Manuel de procédures — écran principal.
// Porté du prototype Claude Design (procedures.jsx). Retirés : l'assistant IA
// (il appelait un service propre à Claude Design) et le sélecteur « Afficher
// en tant que » (outil de démonstration). L'utilisateur est le vrai compte
// connecté, et le stockage est local au navigateur (voir useProcStore.js).

const PROC_VIEW_KEY = "myalfa_procedures_view";
const PROC_TREE_KEY = "myalfa_procedures_tree";

const lsGet = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || fallback;
  } catch (e) {
    return fallback;
  }
};
const lsSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // quota ou stockage indisponible : l'état de navigation n'est pas critique
  }
};

const procSnippet = (text, q) => {
  const n = procNorm(text);
  const i = n.indexOf(procNorm(q));
  if (i < 0) return text.slice(0, 140) + (text.length > 140 ? "…" : "");
  const s = Math.max(0, i - 60);
  const e = Math.min(text.length, i + q.length + 80);
  return (
    <>
      {s > 0 && "…"}
      {text.slice(s, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length, e)}
      {e < text.length && "…"}
    </>
  );
};

const ProceduresScreen = () => {
  const { users, utilisateur } = useEtatGlobal();
  // Les droits et les noms se calculent à partir des vrais comptes : le
  // registre doit être à jour AVANT tout calcul de ce rendu.
  procSetPeople(users, utilisateur);

  const [db, api, etat] = useProcStore();
  const navigate = useNavigate();
  // Lien direct vers une fiche (/home/procedures?page=<id>, partage par mail) :
  // prioritaire sur la dernière vue mémorisée.
  const [ficheUrl] = React.useState(() => new URLSearchParams(window.location.search).get("page"));
  const [view, setView] = React.useState(() => (ficheUrl ? { t: "page", id: ficheUrl } : lsGet(PROC_VIEW_KEY, { t: "home" })));
  const [mode, setMode] = React.useState("read");
  const [isNew, setIsNew] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(() => lsGet(PROC_TREE_KEY, {}));
  const [newModal, setNewModal] = React.useState(null);
  // Rédaction par l'IA en cours : { title, spaceId, sectionId }.
  const [ia, setIa] = React.useState(null);
  const [hist, setHist] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [acc, setAcc] = React.useState(null);
  const [partage, setPartage] = React.useState(false);
  const [ai, setAi] = React.useState(() => lsGet("myalfa_proc_ai_open", false));
  const mainRef = React.useRef(null);
  const toastTimer = React.useRef(null);

  React.useEffect(() => { lsSet(PROC_VIEW_KEY, view); }, [view]);
  React.useEffect(() => { lsSet(PROC_TREE_KEY, open); }, [open]);
  React.useEffect(() => { lsSet("myalfa_proc_ai_open", ai); }, [ai]);
  React.useEffect(() => { if (mainRef.current) mainRef.current.scrollTop = 0; }, [view.t, view.id]);
  // L'URL suit la fiche ouverte : copiable telle quelle depuis la barre
  // d'adresse (Home.js conserve ce paramètre en recalculant l'URL de la vue).
  React.useEffect(() => {
    const cible = "/home/procedures" + (view.t === "page" && view.id ? `?page=${encodeURIComponent(view.id)}` : "");
    if (window.location.pathname + window.location.search !== cible) {
      navigate(cible, { replace: true });
    }
  }, [view.t, view.id, navigate]);

  const flash = React.useCallback((m) => {
    setToast(m);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }, []);

  const canR = (p) => procCan(db, p, "read");
  const canE = (p) => procCan(db, p, "edit");
  const canM = (p) => procCan(db, p, "manage");
  const live = db.pages
    .filter((p) => !p.trashed && canR(p))
    .sort((a, b) => procCur(a).title.localeCompare(procCur(b).title, "fr", { numeric: true }));
  const editSpaces = db.spaces.filter((s) => procCanSpace(db, s.id, "edit"));
  const isManager = db.spaces.some((s) => procCanSpace(db, s.id, "manage"));

  const go = async (v) => {
    if (mode === "edit") {
      const ok = await dialog.confirm("Quitter l'éditeur sans enregistrer ?", {
        variant: "warning", title: "Modifications non enregistrées", confirmLabel: "Quitter", cancelLabel: "Rester",
      });
      if (!ok) return;
    }
    setMode("read");
    setIsNew(false);
    setQ("");
    setView(v);
    if (v.t === "page") {
      const p = db.pages.find((x) => x.id === v.id);
      if (p) setOpen((o) => ({ ...o, [p.spaceId]: true }));
    }
  };
  const page = view.t === "page" ? db.pages.find((p) => p.id === view.id) : null;
  if (view.t === "page" && !page && !etat.chargement) setTimeout(() => setView({ t: "home" }));
  // Arrivée par un lien direct : déplie l'espace de la fiche dans l'arbre.
  const ficheDepliee = React.useRef(false);
  React.useEffect(() => {
    if (!ficheUrl || ficheDepliee.current || !page || page.id !== ficheUrl) return;
    ficheDepliee.current = true;
    setOpen((o) => ({ ...o, [page.spaceId]: true }));
  }, [ficheUrl, page]);

  const ouvrirNouvellePage = (id, spaceId) => {
    setOpen((o) => ({ ...o, [spaceId]: true }));
    setView({ t: "page", id });
    setMode("edit");
    setIsNew(true);
  };

  const createPage = async ({ title, spaceId, sectionId, tpl }) => {
    if (tpl === "ia") {
      setNewModal(null);
      setIa({ title, spaceId, sectionId });
      return;
    }
    const html = PROC_TEMPLATES.find((t) => t.id === tpl).html;
    const id = await api.create({ title, spaceId, sectionId, html });
    if (!id) return;
    setNewModal(null);
    ouvrirNouvellePage(id, spaceId);
  };

  const qTrim = q.trim();
  const results = qTrim.length >= 2
    ? live
        .map((p) => {
          const c = procCur(p);
          const text = procText(c.html);
          const nq = procNorm(qTrim);
          const inT = procNorm(c.title).includes(nq);
          const inB = procNorm(text).includes(nq);
          const inTag = p.tags.some((t) => procNorm(t).includes(nq));
          return inT || inB || inTag ? { p, c, text, score: (inT ? 3 : 0) + (inTag ? 2 : 0) + (inB ? 1 : 0) } : null;
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score)
    : null;

  const spaceOf = (id) => db.spaces.find((s) => s.id === id) || {};
  const sectionOf = (id) => db.sections.find((s) => s.id === id) || {};
  const PageLink = ({ p, sub }) => {
    const c = procCur(p);
    const sp = spaceOf(p.spaceId);
    return (
      <button className="pr-pl" onClick={() => go({ t: "page", id: p.id })}>
        <span className="pr-pl-dot" style={{ background: sp.color }}></span>
        <span className="pr-pl-main">
          <span className="pr-pl-t">{c.title}</span>
          <span className="pr-pl-s">{sub || <>{sp.name} · {sectionOf(p.sectionId).name}</>}</span>
        </span>
        {p.status !== "publie" && <ProcStatus s={p.status} />}
      </button>
    );
  };

  /* ---------- Barre latérale ---------- */
  const Tree = () => (
    <aside className="pr-side">
      <div className="pr-side-head">
        <span className="pr-side-logo"><PrIco n="book" s={18} /></span>
        <div>
          <div className="pr-side-t">Procédures</div>
          <div className="pr-side-s">Manuel du cabinet</div>
        </div>
        {editSpaces.length > 0 && (
          <button
            className="pr-new-plus"
            title="Nouvelle page"
            aria-label="Nouvelle page"
            onClick={() => {
              const pref = view.t === "space" ? view.id : page ? page.spaceId : null;
              setNewModal({ spaceId: editSpaces.some((s) => s.id === pref) ? pref : editSpaces[0].id });
            }}
          >
            <Icon name="plus" size={17} />
          </button>
        )}
      </div>
      <div className="pr-search">
        <Icon name="search" size={15} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une procédure…" />
        {q && <button onClick={() => setQ("")}><Icon name="x" size={13} /></button>}
      </div>
      <nav className="pr-nav">
        {[["home", "Accueil", "home"], ["activity", "Activité", "activity"], ["favs", "Favoris", "star"]].map(([t, l, i]) => (
          <button key={t} className={`pr-nav-i ${view.t === t && !qTrim ? "on" : ""}`} onClick={() => go({ t })}>
            <PrIco n={i} s={15} /> {l}
            {t === "favs" && <span className="pr-nav-n">{db.favs.filter((f) => live.some((p) => p.id === f)).length}</span>}
          </button>
        ))}
      </nav>
      <div className="pr-tree-h">Espaces</div>
      <div className="pr-tree">
        {db.spaces.map((sp) => {
          const pages = live.filter((p) => p.spaceId === sp.id);
          const isO = !!open[sp.id];
          return (
            <div key={sp.id} className="pr-tsp">
              <div className={`pr-tsp-row ${view.t === "space" && view.id === sp.id && !qTrim ? "on" : ""}`}>
                <button className={`pr-chev ${isO ? "open" : ""}`} onClick={() => setOpen((o) => ({ ...o, [sp.id]: !isO }))}>
                  <Icon name="chev" size={13} />
                </button>
                <button className="pr-tsp-name" onClick={() => { go({ t: "space", id: sp.id }); setOpen((o) => ({ ...o, [sp.id]: true })); }}>
                  {sp.name}
                </button>
                <span className="pr-tsp-n">{pages.length}</span>
              </div>
              {isO && db.sections.filter((s) => s.spaceId === sp.id).map((sec) => (
                <div key={sec.id} className="pr-tsec">
                  <div className="pr-tsec-name">{sec.name}</div>
                  {pages.filter((p) => p.sectionId === sec.id).map((p) => (
                    <button
                      key={p.id}
                      className={`pr-tpg ${page && page.id === p.id && !qTrim ? "on" : ""}`}
                      onClick={() => go({ t: "page", id: p.id })}
                      title={procCur(p).title}
                    >
                      <span className="pr-tpg-t">{procCur(p).title}</span>
                      {p.status === "brouillon" && <span className="pr-tpg-b" title="Brouillon"></span>}
                      {p.status === "revision" && <span className="pr-tpg-b warn" title="À réviser"></span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {isManager && (
        <button className={`pr-nav-i pr-trash ${view.t === "trash" ? "on" : ""}`} onClick={() => go({ t: "trash" })}>
          <PrIco n="trash" s={15} /> Corbeille<span className="pr-nav-n">{db.pages.filter((p) => p.trashed).length}</span>
        </button>
      )}
    </aside>
  );

  /* ---------- Vues ---------- */
  const Home = () => {
    const trackRef = React.useRef(null);
    const [edge, setEdge] = React.useState({ l: false, r: true, i: 0 });
    const sync = () => {
      const t = trackRef.current;
      if (!t) return;
      const w = t.firstChild ? t.firstChild.getBoundingClientRect().width + 16 : 1;
      setEdge({ l: t.scrollLeft > 4, r: t.scrollLeft + t.clientWidth < t.scrollWidth - 4, i: Math.round(t.scrollLeft / w) });
    };
    const slide = (d) => {
      const t = trackRef.current;
      const w = t.firstChild.getBoundingClientRect().width + 16;
      t.scrollBy({ left: d * w, behavior: "smooth" });
    };
    const to = (i) => {
      const t = trackRef.current;
      const w = t.firstChild.getBoundingClientRect().width + 16;
      t.scrollTo({ left: i * w, behavior: "smooth" });
    };
    React.useEffect(() => {
      sync();
      const t = trackRef.current;
      t.addEventListener("scroll", sync, { passive: true });
      window.addEventListener("resize", sync);
      return () => {
        t.removeEventListener("scroll", sync);
        window.removeEventListener("resize", sync);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const openSp = (id) => {
      go({ t: "space", id });
      setOpen((o) => ({ ...o, [id]: true }));
    };
    return (
      <div className="pr-page pr-home">
        <div className="pr-home-intro">
          <h1>Manuel de procédures</h1>
          <p>Le manuel est organisé en {db.spaces.length} espaces, chacun découpé en sections qui regroupent les procédures.</p>
        </div>
        <div className="pr-car">
          {edge.l && <button className="pr-car-nav l" onClick={() => slide(-1)} aria-label="Précédent"><Icon name="chev" size={18} /></button>}
          <div className="pr-car-track" ref={trackRef}>
            {db.spaces.map((sp) => {
              const ps = live.filter((p) => p.spaceId === sp.id);
              const secs = db.sections.filter((x) => x.spaceId === sp.id);
              return (
                <div key={sp.id} className="pr-tile" style={{ "--sp": sp.color }}>
                  <button className="pr-tile-top" onClick={() => openSp(sp.id)}>
                    <span className="pr-tile-ico"><PrIco n={PROC_SPACE_ICON[sp.id] || "book"} s={30} /></span>
                    <span className="pr-tile-name">{sp.name}</span>
                    <span className="pr-tile-desc">{sp.desc}</span>
                  </button>
                  <div className="pr-tile-secs">
                    {secs.map((x) => {
                      const n = ps.filter((p) => p.sectionId === x.id).length;
                      return (
                        <button key={x.id} className="pr-tile-sec" onClick={() => openSp(sp.id)}>
                          <span>{x.name}</span><small>{n}</small>
                        </button>
                      );
                    })}
                  </div>
                  <button className="pr-tile-go" onClick={() => openSp(sp.id)}>
                    <span>{`${ps.length} procédure${ps.length > 1 ? "s" : ""}`}</span><Icon name="chev" size={14} />
                  </button>
                </div>
              );
            })}
          </div>
          {edge.r && <button className="pr-car-nav r" onClick={() => slide(1)} aria-label="Suivant"><Icon name="chev" size={18} /></button>}
        </div>
        {(edge.l || edge.r) && (
          <div className="pr-car-dots">
            {db.spaces.map((sp, i) => <button key={sp.id} className={i === edge.i ? "on" : ""} onClick={() => to(i)} aria-label={sp.name}></button>)}
          </div>
        )}
      </div>
    );
  };

  const Space = () => {
    const sp = spaceOf(view.id);
    const [adding, setAdding] = React.useState(false);
    const [nm, setNm] = React.useState("");
    const creerSection = async () => {
      if (await api.addSection(sp.id, nm.trim())) {
        setAdding(false);
        setNm("");
      }
    };
    const ps = live.filter((p) => p.spaceId === sp.id);
    return (
      <div className="pr-page">
        <div className="pr-crumb">
          <button onClick={() => go({ t: "home" })}>Procédures</button><Icon name="chev" size={12} /><span>{sp.name}</span>
        </div>
        <div className="pr-sp-head">
          <span className="pr-sp-sq" style={{ background: sp.color }}><PrIco n="book" s={22} /></span>
          <div className="pr-sp-txt"><h1>{sp.name}</h1><p>{sp.desc}</p></div>
          {procCanSpace(db, sp.id, "manage") && (
            <button className="btn" onClick={() => setAcc({ kind: "space", id: sp.id })}><PrIco n="lock" s={14} /> Accès</button>
          )}
          {procCanSpace(db, sp.id, "edit") && (
            <button className="btn" onClick={() => setAdding(true)}><Icon name="plus" size={14} /> Section</button>
          )}
          {procCanSpace(db, sp.id, "edit") && (
            <button className="btn primary" onClick={() => setNewModal({ spaceId: sp.id })}><Icon name="plus" size={14} /> Nouvelle page</button>
          )}
        </div>
        {adding && (
          <div className="pr-addsec">
            <input
              autoFocus
              value={nm}
              onChange={(e) => setNm(e.target.value)}
              placeholder="Nom de la section"
              onKeyDown={(e) => {
                if (e.key === "Enter" && nm.trim()) { creerSection(); }
                if (e.key === "Escape") setAdding(false);
              }}
            />
            <button className="btn primary" disabled={!nm.trim()} onClick={creerSection}>Créer</button>
            <button className="btn" onClick={() => setAdding(false)}>Annuler</button>
          </div>
        )}
        {db.sections.filter((s) => s.spaceId === sp.id).map((sec) => {
          const sp2 = ps.filter((p) => p.sectionId === sec.id);
          return (
            <section key={sec.id} className="pr-sec">
              <div className="pr-sec-h">
                <span>{sec.name}</span><span className="pr-count">{sp2.length}</span>
                {procCanSpace(db, sp.id, "edit") && (
                  <button className="pr-link" onClick={() => setNewModal({ spaceId: sp.id, sectionId: sec.id })}>
                    <Icon name="plus" size={12} /> Ajouter une page
                  </button>
                )}
              </div>
              {sp2.length ? (
                <div className="pr-tbl">
                  {sp2.map((p) => {
                    const c = procCur(p);
                    return (
                      <button key={p.id} className="pr-tr" onClick={() => go({ t: "page", id: p.id })}>
                        <span className="pr-tr-t">
                          {c.title}
                          {p.acl && procIsRestricted(p.acl) && <span className="pr-lock" title="Accès restreint"><PrIco n="lock" s={12} /></span>}
                        </span>
                        <ProcStatus s={p.status} />
                        <span className="pr-tr-who"><ProcAv id={c.author} size={20} /> {procName(c.author)}</span>
                        <span className="pr-tr-d">{procRel(c.date)}</span>
                        <span className="pr-tr-v">v{c.v}</span>
                      </button>
                    );
                  })}
                </div>
              ) : <div className="pr-empty">Aucune page dans cette section.</div>}
            </section>
          );
        })}
      </div>
    );
  };

  const PageView = () => {
    const c = procCur(page);
    const sp = spaceOf(page.spaceId);
    const first = page.versions[0];
    const { html, toc } = React.useMemo(
      () => procWithToc(c.html, (id) => {
        const p = live.find((x) => x.id === id);
        return p ? procCur(p).title : null;
      }),
      // `live` change à chaque rendu (nouveau tableau) : on ne suit que sa
      // longueur, comme dans le prototype, pour ne pas recalculer le sommaire
      // à chaque rendu.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [c.html, live.length]
    );
    const outLinks = [...new Set([...c.html.matchAll(/data-page="([^"]+)"/g)].map((m) => m[1]))]
      .map((id) => live.find((p) => p.id === id))
      .filter(Boolean);
    const backLinks = live.filter((p) => p.id !== page.id && procCur(p).html.includes(`data-page="${page.id}"`));
    const [zoom, setZoom] = React.useState(null);
    const onContentClick = (e) => {
      if (e.target.tagName === "IMG") { setZoom(e.target.getAttribute("src")); return; }
      const a = e.target.closest && e.target.closest("a.pr-xref");
      if (!a) return;
      e.preventDefault();
      if (!a.classList.contains("is-broken")) go({ t: "page", id: a.dataset.page });
    };
    const [menu, setMenu] = React.useState(false);
    // Identifiants normalisés : un auteur de la graine ("ha") et son vrai compte
    // sont la même personne, un seul avatar.
    const contrib = [...new Set(page.versions.map((v) => procWho(v.author).id))];
    const fav = db.favs.includes(page.id);
    const overdue = page.reviewDate && page.reviewDate < Date.now();
    const jump = (id) => {
      const el = mainRef.current.querySelector("#" + id);
      if (el) mainRef.current.scrollTo({ top: el.offsetTop - 20, behavior: "smooth" });
    };
    // Le contenu des anciennes versions ne se charge qu'à l'ouverture de l'historique.
    const ouvrirHist = async () => {
      if (await api.loadVersions(page.id)) setHist(true);
    };
    const supprimer = async () => {
      const ok = await dialog.confirm("Placer cette page dans la corbeille ? Elle pourra être restaurée avec tout son historique.", {
        variant: "danger", title: "Supprimer la page", confirmLabel: "Placer dans la corbeille",
      });
      if (!ok) return;
      if (!(await api.trash(page.id))) return;
      setMenu(false);
      go({ t: "space", id: sp.id });
      flash("Page placée dans la corbeille");
    };
    return (
      <div className="pr-read">
        <article className="pr-article">
          <div className="pr-crumb">
            <button onClick={() => go({ t: "home" })}>Procédures</button><Icon name="chev" size={12} />
            <button onClick={() => go({ t: "space", id: sp.id })}>{sp.name}</button><Icon name="chev" size={12} />
            <span>{sectionOf(page.sectionId).name}</span>
          </div>
          {page.trashed && (
            <div className="pr-banner bad">
              <PrIco n="trash" s={15} /> Cette page est dans la corbeille.{" "}
              <button className="pr-link" onClick={async () => { if (await api.trash(page.id, false)) flash("Page restaurée"); }}>Restaurer</button>
            </div>
          )}
          {overdue && !page.trashed && (
            <div className="pr-banner warn">
              <Icon name="alert" size={15} /> <span>{`Révision prévue le ${procFmt(page.reviewDate, false)} — à relire par ${procName(page.owner)}.`}</span>
            </div>
          )}
          <div className="pr-title-row">
            <h1 className="pr-title">{c.title}</h1>
            <div className="pr-actions">
              <button className={`pr-ibtn ${fav ? "fav" : ""}`} title={fav ? "Retirer des favoris" : "Ajouter aux favoris"} onClick={() => api.toggleFav(page.id)}><PrIco n="star" s={16} /></button>
              {!page.trashed && <button className="pr-ibtn" title="Partager" aria-label="Partager" onClick={() => setPartage(true)}><PrIco n="share" s={16} /></button>}
              <button className="pr-ibtn" title={`Historique ·${page.versions.length} version${page.versions.length > 1 ? "s" : ""}`} onClick={ouvrirHist}><PrIco n="history" s={16} /></button>
              {canM(page) && <button className="pr-ibtn" title="Gérer les accès" onClick={() => setAcc({ kind: "page", id: page.id })}><PrIco n="lock" s={16} /></button>}
              {!page.trashed && canE(page) && <button className="btn pr-edit-btn" onClick={() => setMode("edit")}><Icon name="pencil" size={14} /> Modifier</button>}
              <div className="pr-menu-wrap">
                {canE(page) && <button className="pr-ibtn" onClick={() => setMenu(!menu)}><Icon name="dots" size={16} /></button>}
                {menu && (
                  <div className="pr-menu" onMouseLeave={() => setMenu(false)}>
                    <button onClick={async () => { const id = await api.duplicate(page.id); setMenu(false); if (id) { go({ t: "page", id }); flash("Page dupliquée"); } }}><PrIco n="copy" s={14} /> Dupliquer</button>
                    {page.status !== "revision" && (
                      <button onClick={async () => { setMenu(false); if (await api.setStatus(page.id, "revision")) flash("Page marquée « À réviser »"); }}><Icon name="alert" size={14} /> Marquer à réviser</button>
                    )}
                    {!page.trashed && <button className="danger" onClick={supprimer}><PrIco n="trash" s={14} /> Supprimer</button>}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="pr-meta">
            {page.status !== "publie" && <ProcStatus s={page.status} />}
            {procIsRestricted(procAclOf(db, page)) && <span className="pr-meta-lock"><PrIco n="lock" s={12} /> Accès restreint</span>}
            {!canE(page) && <span className="pr-meta-lock">Lecture seule</span>}
            <button className="pr-meta-v" onClick={ouvrirHist} title="Voir l'historique">{`Mis à jour ${procRel(c.date)} par ${procName(c.author)} · v${c.v}`}</button>
          </div>
          <div className="pr-content" onClick={onContentClick} dangerouslySetInnerHTML={{ __html: html }}></div>
          {zoom && (
            <div className="pr-zoom" onClick={() => setZoom(null)}>
              <img src={zoom} alt="" draggable="false" />
              <button className="pr-zoom-x" aria-label="Fermer"><Icon name="x" size={18} /></button>
            </div>
          )}
          <div className="pr-foot">
            Créé par {procName(first.author)} le {procFmt(first.date, false)} · Responsable : {procName(page.owner)}
            {page.reviewDate && <> · Révision le {procFmt(page.reviewDate, false)}</>}
          </div>
        </article>
        <aside className="pr-rail">
          {toc.length > 1 && (
            <div className="pr-rail-card">
              <div className="pr-rail-h">Sommaire</div>
              {toc.map((t) => <button key={t.id} className={`pr-toc l${t.lvl}`} onClick={() => jump(t.id)}>{t.t}</button>)}
            </div>
          )}
          {(outLinks.length > 0 || backLinks.length > 0) && (
            <div className="pr-rail-card pr-links">
              {outLinks.length > 0 && (
                <>
                  <div className="pr-rail-h">Renvoie vers</div>
                  {outLinks.map((p) => <button key={p.id} className="pr-toc" onClick={() => go({ t: "page", id: p.id })}>{procCur(p).title}</button>)}
                </>
              )}
              {backLinks.length > 0 && (
                <>
                  <div className="pr-rail-h">Cité dans</div>
                  {backLinks.map((p) => <button key={p.id} className="pr-toc" onClick={() => go({ t: "page", id: p.id })}>{procCur(p).title}</button>)}
                </>
              )}
            </div>
          )}
          <div className="pr-rail-card pr-rail-quiet">
            <span className="pr-avs" title="Contributeurs">{contrib.map((a) => <ProcAv key={a} id={a} size={20} />)}</span>
            {page.tags.length > 0 && (
              <div className="pr-tags">{page.tags.map((t) => <button key={t} className="pr-tag" onClick={() => setQ(t)}>#{t}</button>)}</div>
            )}
          </div>
        </aside>
      </div>
    );
  };

  const Activity = () => {
    const [who, setWho] = React.useState("all");
    const [type, setType] = React.useState("all");
    const vis = db.log.filter((e) => {
      if (!e.pageId) return true;
      const p = db.pages.find((x) => x.id === e.pageId);
      return !p || canR(p);
    });
    const authors = [...new Set(vis.map((e) => procWho(e.author).id))];
    const list = vis.filter((e) =>
      (who === "all" || procWho(e.author).id === who) &&
      (type === "all" || e.type === type || (type === "restore" && e.type === "undelete")));
    const groups = [];
    list.forEach((e) => {
      const k = new Date(e.date).toDateString();
      let g = groups.find((x) => x.k === k);
      if (!g) groups.push((g = { k, d: e.date, items: [] }));
      g.items.push(e);
    });
    const dayLab = (d) => {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      const n = (t - x) / 86400000;
      return n === 0 ? "Aujourd'hui" : n === 1 ? "Hier" : x.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    };
    return (
      <div className="pr-page">
        <div className="pr-home-head"><h1>Activité</h1><p>Journal des créations et modifications par auteur</p></div>
        <div className="pr-filters">
          <div className="pr-seg">
            {PROC_EV_FILTERS.map(([k, l]) => <button key={k} className={type === k ? "on" : ""} onClick={() => setType(k)}>{l}</button>)}
          </div>
          <select className="pr-f-in pr-who" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="all">Tous les auteurs</option>
            {authors.map((a) => <option key={a} value={a}>{procWho(a).name}</option>)}
          </select>
          <span className="pr-f-count">{list.length} évènement{list.length > 1 ? "s" : ""}</span>
        </div>
        <div className="pr-contrib">
          {authors.map((a) => {
            const n = vis.filter((e) => procWho(e.author).id === a).length;
            return (
              <button key={a} className={`pr-contrib-i ${who === a ? "on" : ""}`} onClick={() => setWho(who === a ? "all" : a)}>
                <ProcAv id={a} size={26} /><span><b>{procName(a)}</b><small>{n} contribution{n > 1 ? "s" : ""}</small></span>
              </button>
            );
          })}
        </div>
        {groups.map((g) => (
          <section key={g.k} className="pr-day">
            <div className="pr-day-h">{dayLab(g.d)}</div>
            <div className="pr-box pr-box-flush">
              {g.items.map((e) => {
                const exists = e.pageId && db.pages.some((p) => p.id === e.pageId);
                return (
                  <button key={e.id} className="pr-feed" disabled={!exists} onClick={() => exists && go({ t: "page", id: e.pageId })}>
                    <span className="pr-feed-time">{new Date(e.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                    <ProcAv id={e.author} size={28} />
                    <span className="pr-feed-main">
                      <span className="pr-feed-l"><b>{procWho(e.author).name}</b> {PROC_EV[e.type].lab} <span className="pr-feed-t">{e.title}</span></span>
                      <span className="pr-feed-s">{e.detail}</span>
                    </span>
                    <span className={`pr-ev tone-${PROC_EV[e.type].tone}`}>
                      {e.v ? "v" + e.v : PROC_EV_FILTERS.find(([k]) => k === e.type) ? PROC_EV_FILTERS.find(([k]) => k === e.type)[1].replace(/s$/, "") : "—"}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
        {!groups.length && <div className="pr-empty">Aucun évènement pour ce filtre.</div>}
      </div>
    );
  };

  const ListView = ({ title, sub, pages, empty, trash }) => (
    <div className="pr-page">
      <div className="pr-home-head"><h1>{title}</h1><p>{sub}</p></div>
      <div className="pr-box pr-box-flush">
        {pages.length
          ? pages.map((p) => (trash ? (
            <div key={p.id} className="pr-trash-row">
              <PageLink p={p} sub={<>Supprimée {procRel(p.trashed)}</>} />
              <button className="btn" onClick={async () => { if (await api.trash(p.id, false)) flash("Page restaurée"); }}><PrIco n="restore" s={14} /> Restaurer</button>
            </div>
          ) : <PageLink key={p.id} p={p} />))
          : <div className="pr-empty">{empty}</div>}
      </div>
    </div>
  );

  const Results = () => (
    <div className="pr-page">
      <div className="pr-home-head">
        <h1>Résultats pour « {qTrim} »</h1>
        <p>{results.length} procédure{results.length > 1 ? "s" : ""} trouvée{results.length > 1 ? "s" : ""}</p>
      </div>
      <div className="pr-results">
        {results.map(({ p, c, text }) => {
          const sp = spaceOf(p.spaceId);
          return (
            <button key={p.id} className="pr-res" onClick={() => go({ t: "page", id: p.id })}>
              <span className="pr-res-path"><span className="pr-sq" style={{ background: sp.color }}></span>{sp.name} · {sectionOf(p.sectionId).name}</span>
              <span className="pr-res-t">{c.title}</span>
              <span className="pr-res-x">{procSnippet(text, qTrim)}</span>
              <span className="pr-res-m">Modifié par {procName(c.author)} {procRel(c.date)} · v{c.v}</span>
            </button>
          );
        })}
        {!results.length && <div className="pr-empty">Aucun résultat. Essayez un autre mot-clé.</div>}
      </div>
    </div>
  );

  let body;
  if (!db.spaces.length && (etat.chargement || etat.erreur)) {
    body = (
      <div className="pr-page pr-home">
        {etat.erreur ? (
          <div className="pr-noacc">
            <h2>Chargement impossible</h2>
            <p>{etat.erreur}</p>
            <button className="btn primary" onClick={etat.recharger}>Réessayer</button>
          </div>
        ) : (
          <div aria-hidden="true">
            <div className="pr-skel-b" style={{ width: 320, height: 28, margin: "0 auto 14px" }} />
            <div className="pr-skel-b" style={{ width: 460, height: 14, margin: "0 auto 36px" }} />
            <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
              {[0, 1, 2, 3].map((i) => <div key={i} className="pr-skel-b" style={{ width: 230, height: 260, borderRadius: 16 }} />)}
            </div>
          </div>
        )}
      </div>
    );
  } else if (results) body = <Results />;
  else if (view.t === "page" && page && mode === "edit" && canE(page)) {
    body = (
      <ProcEditor
        key={page.id + "-" + procCur(page).v}
        page={page}
        db={db}
        isNew={isNew}
        onCancel={() => { setMode("read"); setIsNew(false); }}
        onSave={async (d) => {
          const modifie = d.title !== procCur(page).title || d.html !== procCur(page).html;
          const v = procCur(page).v + 1;
          if (!(await api.save(page.id, d))) return;
          setMode("read");
          setIsNew(false);
          flash(modifie ? `Version ${v} enregistrée` : "Informations mises à jour");
        }}
      />
    );
  } else if (view.t === "page" && page && !canR(page)) {
    body = (
      <div className="pr-page">
        <div className="pr-noacc">
          <span><PrIco n="lock" s={26} /></span>
          <h2>Accès restreint</h2>
          <p>Vous n'avez pas les droits de lecture sur cette procédure. Contactez {procName(page.owner)}, responsable de la page.</p>
          <button className="btn" onClick={() => go({ t: "home" })}>Retour à l'accueil</button>
        </div>
      </div>
    );
  } else if (view.t === "page" && page) body = <PageView />;
  else if (view.t === "space") body = <Space />;
  else if (view.t === "activity") body = <Activity />;
  else if (view.t === "favs") body = <ListView title="Favoris" sub="Vos procédures épinglées" pages={live.filter((p) => db.favs.includes(p.id))} empty="Ajoutez une procédure à vos favoris avec l'étoile." />;
  else if (view.t === "trash") body = <ListView trash title="Corbeille" sub="Les pages supprimées peuvent être restaurées avec tout leur historique" pages={db.pages.filter((p) => p.trashed)} empty="La corbeille est vide." />;
  else body = <Home />;

  // Les procédures ne se copient, ne s'impriment et ne s'enregistrent pas
  // (choix du prototype) : on neutralise Ctrl+P / Ctrl+S et l'impression tant
  // que l'écran est ouvert.
  React.useEffect(() => {
    const k = (e) => {
      if ((e.ctrlKey || e.metaKey) && ((e.key || "").toLowerCase() === "p" || (e.key || "").toLowerCase() === "s")) {
        e.preventDefault();
        flash("L'impression et l'enregistrement des procédures sont désactivés");
      }
    };
    const bp = () => document.body.classList.add("pr-noprint");
    window.addEventListener("keydown", k, true);
    window.addEventListener("beforeprint", bp);
    document.body.classList.add("pr-noprint");
    return () => {
      window.removeEventListener("keydown", k, true);
      window.removeEventListener("beforeprint", bp);
      document.body.classList.remove("pr-noprint");
    };
  }, [flash]);

  const aiPage = view.t === "page" && page && !page.trashed && canR(page) ? page : null;

  const guard = (e) => {
    if (e.target.closest && e.target.closest(".pr-editable, input, textarea")) return;
    e.preventDefault();
    if (e.type !== "dragstart") flash("La copie du contenu des procédures est désactivée");
  };

  return (
    <div className={`pr ${ai ? "has-ai" : ""}`} onCopy={guard} onCut={guard} onContextMenu={guard} onDragStart={guard}>
      {Tree()}
      <main className={`pr-main ${mode === "edit" && !results ? "is-edit" : ""}`} ref={mainRef}>{body}</main>
      {acc && (
        <ProcAccessModal
          db={db}
          target={acc}
          onClose={() => setAcc(null)}
          onSave={async (acl) => { if (await api.setAcl(acc.kind, acc.id, acl)) { setAcc(null); flash("Accès mis à jour"); } }}
        />
      )}
      {ai && (
        <ProcAssistant
          cfg={{
            corpus: "proc",
            cle: "myalfa_proc_ai_v1",
            titre: "Assistant procédures",
            sousTitre: `Répond à partir des ${live.length} pages du manuel`,
            intro: "Posez une question sur une procédure du cabinet. Les réponses s'appuient uniquement sur le manuel et indiquent les pages sources.",
            sugg: aiPage
              ? ["Résume cette procédure en 3 points", "Quels sont les points de vigilance ?", "Quels documents dois-je réunir avant de commencer ?"]
              : ["Quel délai de traitement dois-je respecter ?", "Que faire en présence d'un doublon de compte fournisseur ?", "À partir de quel montant rechercher une immobilisation ?", "Que doit contenir la restitution au client ?"],
            note: "Vérifiez toujours la procédure source avant d'agir.",
          }}
          contexte={aiPage ? { id: aiPage.id, titre: procCur(aiPage).title } : null}
          onOpen={(src) => go({ t: "page", id: src.id })}
          onClose={() => setAi(false)}
        />
      )}
      {!ai && !(mode === "edit" && view.t === "page") && <ProcMascot onOpen={() => setAi(true)} />}
      {newModal && <ProcNewModal spaces={editSpaces} db={db} init={newModal} ia={procIaAutorise(utilisateur)} onClose={() => setNewModal(null)} onCreate={createPage} />}
      {ia && (
        <ProcRedactionIA
          init={ia}
          emplacement={[(db.spaces.find((s) => s.id === ia.spaceId) || {}).name, (db.sections.find((s) => s.id === ia.sectionId) || {}).name].filter(Boolean).join(" › ")}
          onClose={() => setIa(null)}
          onCreer={async (html) => {
            const id = await api.create({ title: ia.title, spaceId: ia.spaceId, sectionId: ia.sectionId, html, origine: "ia" });
            if (!id) return false;
            setIa(null);
            ouvrirNouvellePage(id, ia.spaceId);
            return true;
          }}
        />
      )}
      {partage && page && (
        <ProcPartageModal
          db={db}
          page={page}
          users={users}
          onClose={() => setPartage(false)}
          onSent={({ envoyes, echecs }) => {
            setPartage(false);
            flash(echecs && echecs.length
              ? `Envoyée à ${envoyes} personne${envoyes > 1 ? "s" : ""} — échec pour ${echecs.join(", ")}`
              : `Procédure partagée avec ${envoyes} personne${envoyes > 1 ? "s" : ""}`);
          }}
        />
      )}
      {hist && page && (
        <ProcHistory
          page={page}
          onClose={() => setHist(false)}
          onRestore={async (v) => { const n = procCur(page).v + 1; if (await api.restore(page.id, v)) flash(`Version ${v} restaurée (nouvelle v${n})`); }}
        />
      )}
      {toast && <div className="pr-toast"><Icon name="check" size={14} /> {toast}</div>}
    </div>
  );
};

const ProcNewModal = ({ db, spaces, init, ia, onClose, onCreate }) => {
  const [title, setTitle] = React.useState("");
  const [spaceId, setSpaceId] = React.useState(init.spaceId);
  const secs = db.sections.filter((s) => s.spaceId === spaceId);
  const [sectionId, setSectionId] = React.useState(init.sectionId || (secs[0] || {}).id);
  const [tpl, setTpl] = React.useState("proc");
  const ok = title.trim() && sectionId;
  return (
    <div className="scrim" onClick={onClose}>
      <div className="modal pr-new-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="icon-square"><PrIco n="book" s={22} /></div>
          <div>
            <h2>Nouvelle procédure</h2>
            <div className="meta"><span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>La page est créée en brouillon</span></div>
          </div>
          <div className="actions"><button className="x-btn" onClick={onClose}><Icon name="x" size={16} /></button></div>
        </div>
        <div className="modal-body pr-new-body">
          <label className="pr-f-lab">Titre <span className="req">*</span></label>
          <input
            autoFocus
            className="pr-f-in pr-f-big"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex. : Déclaration de TVA mensuelle"
            onKeyDown={(e) => e.key === "Enter" && ok && onCreate({ title: title.trim(), spaceId, sectionId, tpl })}
          />
          <div className="pr-new-2">
            <div>
              <label className="pr-f-lab">Espace</label>
              <select
                className="pr-f-in"
                value={spaceId}
                onChange={(e) => {
                  setSpaceId(e.target.value);
                  setSectionId((db.sections.find((s) => s.spaceId === e.target.value) || {}).id);
                }}
              >
                {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="pr-f-lab">Section</label>
              <select className="pr-f-in" value={sectionId || ""} onChange={(e) => setSectionId(e.target.value)}>
                {secs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <label className="pr-f-lab">Modèle</label>
          <div className="pr-tpls">
            {PROC_TEMPLATES.map((t) => (
              <button key={t.id} className={`pr-tpl ${tpl === t.id ? "on" : ""}`} onClick={() => setTpl(t.id)}>
                <span className="pr-tpl-n">{t.name}</span><span className="pr-tpl-d">{t.desc}</span>
              </button>
            ))}
            {/* Rédaction par l'IA (accès réservé) : à partir d'une consigne et de documents. */}
            {ia && (
              <button className={`pr-tpl pr-tpl-ia ${tpl === "ia" ? "on" : ""}`} onClick={() => setTpl("ia")}>
                <span className="pr-tpl-ico"><PrIco n="spark" s={18} /></span>
                <span className="pr-tpl-t">
                  <span className="pr-tpl-n">Rédiger avec l'IA</span>
                  <span className="pr-tpl-d">L'IA prépare le brouillon à partir de votre consigne et de vos documents (PDF, Word, Excel…). Vous le relisez avant de créer la page.</span>
                </span>
              </button>
            )}
          </div>
        </div>
        <div className="pr-new-foot">
          <button className="btn" onClick={onClose}>Annuler</button>
          <button className="btn primary" disabled={!ok} onClick={() => onCreate({ title: title.trim(), spaceId, sectionId, tpl })}>
            {tpl === "ia" ? <><PrIco n="spark" s={14} /> Continuer avec l'IA</> : "Créer et rédiger"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProceduresScreen;
